/*
  owner 2026-09-17: the Styling Videos wall and its player.

  Two jobs in one file so the page loads one script:
    1. the wall loads more videos as you scroll (no pagination links)
    2. tapping a tile opens the video full screen, and you swipe up and down
       through the rest, the way Shorts does

  HOW THE FEED IS BUILT

  Three slides of identical size sit in a column: previous, current, next. EACH
  slide owns its own YouTube player, and the two that are off screen are CUED
  rather than played - YouTube fetches the video and buffers its opening without
  starting it. So when you swipe, the next video is already loaded and begins
  immediately instead of showing a thumbnail while it loads.

  The column is centred with the neighbours' edges showing as slivers, which is
  what makes it obvious there is more to scroll to. A swipe moves the column by
  exactly one slide, so the neighbour lands where the current slide was.

  Then the slides ROTATE by changing their CSS `order`, not by moving them in
  the DOM. This matters: moving an element that contains an iframe reloads it,
  which would throw away the very buffering this design exists for. `order` is
  purely visual, so every player survives. The slide that rotates out of view
  gets cued with the next video along.

  The chrome (buttons, product card, swipe surface) holds no iframe, so it is
  simply moved into whichever slide is centred.

  Other notes for whoever touches this next:

  - THE div:empty TRAP. base.css carries
        a:empty,ul:empty,dl:empty,div:empty,section:empty,...{display:none}
    and the swipe surface is an empty div, so it was display:none and every
    touch fell through to the YouTube iframe, which ignores them. `div:empty` is
    specificity 0,1,1, so a single class cannot beat it: the rule is
    `.sm-player .sm-player__swipe`. Any empty element added here needs the same.

  - WHY A SWIPE SURFACE AT ALL. Touches that land on a cross-origin iframe never
    reach the parent page. That also means YouTube's own controls are
    unreachable, which is why we run controls:0 and provide our own pause and
    mute.

  - MUTED START. Browsers refuse autoplay with sound. Unmuted, the player loads
    and then sits there until the shopper guesses to tap the video.

  - NO requestAnimationFrame for state. rAF does not fire in a background tab,
    so releasing the animation lock inside one left the player permanently
    unresponsive if the visitor switched away mid-swipe.
*/
(function () {
  'use strict';

  if (window.__smWallInit) return;
  window.__smWallInit = true;

  var API = 'https://www.youtube.com/iframe_api';
  var SWIPE = 55;          // px of travel before a drag counts as a swipe
  var TAP_MS = 300;        // a touch shorter than this, barely moved, is a tap
  var SLIDE_MS = 260;      // how long one slide takes to move into place
  var THUMB = 'https://i.ytimg.com/vi/';
  // frame0.jpg is the video's own opening frame, and it is correctly vertical
  // for a Short (268x480, ~25 KB). hqdefault is a mid-video still, so using it
  // as the holding image meant the picture visibly JUMPED the moment playback
  // started. The first frame matches what the video begins on, so the handover
  // is invisible. hqdefault stays as the fallback if frame0 is missing.
  var POSTER = 'frame0.jpg';

  var reduceMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var overlay = null;
  var stack = null;
  var chrome_ = null;
  var slots = [];          // {el, mountId, player, videoId, ready}
  var mid = 1;             // which slot is centred
  var playlist = [];
  var index = 0;
  var lastFocus = null;
  var apiLoading = false;
  var soundOn = false;      // set once the shopper unmutes, so it sticks
  var sliding = false;

  var CSS = [
    'html.sm-player-open{overflow:hidden}',
    /* the padding pulls the column up a little, so the next video peeks by a
       clearly visible amount at the bottom rather than an even hairline on both
       sides - the peek is the whole point of it being there */
    '.sm-player{position:fixed;inset:0;z-index:1000;display:flex;align-items:center;justify-content:center;background:#000;overflow:hidden;padding-bottom:4.4rem}',
    /* an author display:flex beats the browser's own [hidden] rule, so the
       closed state has to be spelled out or the overlay never goes away */
    '.sm-player[hidden]{display:none}',
    '.sm-player__stack{position:relative;display:flex;flex-direction:column;gap:.7rem;will-change:transform}',
    /* Every slide is the same size. On a phone the width is the binding
       constraint, so width leads and 9/16 derives the height; on a desktop there
       is width to spare, so height leads. Setting BOTH breaks the ratio, which
       is what letterboxed every video in the first build. */
    '.sm-player__slide{position:relative;flex:0 0 auto;width:100vw;aspect-ratio:9/16;background:#111;overflow:hidden}',
    '@media (min-width:750px){.sm-player{background:rgba(0,0,0,.94)}',
    '.sm-player__slide{width:auto;height:93vh;max-width:94vw;border-radius:1.2rem}}',
    /* each slide's own still frame, behind its player, so a video that has not
       started yet shows itself rather than black */
    '.sm-player__poster{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block}',
    '.sm-player__poster[hidden]{display:none}',
    '.sm-player__frame{position:absolute;inset:0;z-index:1;opacity:0;transition:opacity .18s ease}',
    '.sm-player__slide.is-playing .sm-player__frame{opacity:1}',
    '.sm-player__frame iframe{position:absolute;inset:0;width:100%;height:100%;border:0}',
    '@media (prefers-reduced-motion:reduce){.sm-player__frame{transition:none}}',
    /* the chrome sits in whichever slide is centred */
    /* The swipe surface deliberately STOPS below YouTube's own top bar, so
       their mute button is reachable. We used to cover it and ship our own,
       which could not be kept honest: each slide has its own player, a fresh
       one always starts muted, so a carried-over "unmuted" label lied about
       half the videos. YouTube's button reports and controls the truth. */
    '.sm-player .sm-player__swipe{position:absolute;top:4.8rem;left:0;right:0;bottom:0;z-index:2;display:block;touch-action:none}',
    /* The wrapper spans the whole slide, so it has to be transparent to
       clicks or it swallows them - including the ones meant for YouTube's own
       mute button in the top bar, which is exactly what happened. Only the
       children that are meant to be touched take pointer events back. */
    '.sm-player__chrome{position:absolute;inset:0;z-index:2;pointer-events:none}',
    '.sm-player__chrome > *{pointer-events:auto}',
    '.sm-player__tap{pointer-events:none}',
    '.sm-player__btn{display:flex;align-items:center;justify-content:center;padding:0;border:0;border-radius:50%;background:rgba(0,0,0,.72);color:#fff;cursor:pointer;-webkit-tap-highlight-color:transparent}',
    '.sm-player__btn svg{width:1.8rem;height:1.8rem;fill:none;stroke:currentColor;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}',
    /* clear of YouTube's own bar across the top of a Shorts embed (its logo
       left, its mute right, about 48px) */
    '.sm-player__close{position:absolute;top:5.6rem;left:1.2rem;z-index:4;width:3.4rem;height:3.4rem}',
    /* Our own mute, always shown. YouTube draws one of TWO layouts over the
       embed and we cannot tell which from outside the iframe: the Shorts layout
       (its logo and mute top-right, video title along the bottom) or the
       classic one (title and avatar top-left, and with controls:0 no buttons at
       all). That is why the mute seemed to come and go. Ours is the one control
       that is always there. Bottom left, clear of the card and of YouTube's own
       top-right corner. */
    '.sm-player__mute{position:absolute;bottom:9.5rem;left:1.2rem;z-index:4;width:3.4rem;height:3.4rem}',
    '.sm-player__mute[hidden]{display:none}',
    /* the pause marker: state comes from the player, never from guessing */
    '.sm-player__tap{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);z-index:3;width:6.4rem;height:6.4rem;border-radius:50%;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;opacity:0;transition:opacity .18s ease;pointer-events:none}',
    '.sm-player__tap svg{width:3rem;height:3rem;fill:#fff}',
    '.sm-player__chrome.is-paused .sm-player__tap{opacity:1}',
    '@media (prefers-reduced-motion:reduce){.sm-player__tap{transition:none}}',
    /* the product card. Solid white, so YouTube's own title overlay cannot make
       it unreadable, and above the swipe surface so it stays tappable. */
    '.sm-player__card{position:absolute;left:.8rem;right:.8rem;bottom:.9rem;z-index:5;display:flex;align-items:center;gap:1rem;padding:.7rem;border-radius:1rem;background:#fff;color:#121212;text-decoration:none;box-shadow:0 2px 14px rgba(0,0,0,.32)}',
    '.sm-player__card[hidden]{display:none}',
    '.sm-player__card-img{flex:0 0 auto;width:4.4rem;height:5.6rem;object-fit:cover;border-radius:.6rem;background:#f2f2f2}',
    '.sm-player__card-img[hidden]{display:none}',
    '.sm-player__card-text{min-width:0;flex:1 1 auto}',
    '.sm-player__card-title{display:block;font-size:1.3rem;line-height:1.3;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}',
    '.sm-player__card-prices{display:flex;align-items:baseline;flex-wrap:wrap;gap:.6rem;margin-top:.3rem}',
    '.sm-player__card-price{font-size:1.6rem;font-weight:700}',
    '.sm-player__card-was{font-size:1.3rem;opacity:.55}',
    '.sm-player__card-was[hidden],.sm-player__card-off[hidden]{display:none}',
    '.sm-player__card-off{padding:.2rem .5rem;border-radius:.4rem;background:#fdecec;color:#b42318;font-size:1.2rem;font-weight:700}',
    '.sm-player__card-go{flex:0 0 auto;width:2.2rem;height:2.2rem;fill:none;stroke:currentColor;stroke-width:2;stroke-linecap:round;stroke-linejoin:round;opacity:.5}',
    '.sm-player__card:focus-visible{outline:2px solid #fff;outline-offset:3px}'
  ].join('\n');

  var ICON = {
    close: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    muted: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M11 5L6 9H3v6h3l5 4V5zM17 9l4 6M21 9l-4 6"/></svg>',
    loud: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M11 5L6 9H3v6h3l5 4V5zM16 9a4 4 0 010 6M19 7a7 7 0 010 10"/></svg>',
    pause: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 5h3v14H8zM13 5h3v14h-3z"/></svg>',
    go: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 6l6 6-6 6"/></svg>'
  };

  function track(name, params) {
    // GA4 is already on every page via Shopify's Google channel, so this costs
    // no extra script - one beacon, only when someone acts.
    try {
      var p = params || {};
      // owner 2026-09-20: our own devices carry a flag set by theme.liquid, so
      // GA4's exclusion filter can drop our testing. Stamped on the event as
      // well as globally, in case the tag loads after the global set() runs.
      if (window.smInternalTraffic) { p = Object.assign({}, p, { traffic_type: 'internal' }); }
      if (typeof window.gtag === 'function') window.gtag('event', name, p);
    } catch (e) { /* analytics must never break the player */ }
  }

  // ── watch depth ────────────────────────────────────────────────────────────
  // owner 2026-09-18: an open only says someone pressed play. It cannot tell a
  // video people watch to the end from one they skip in a second, which is the
  // whole question when deciding what to film next.
  //
  // One beacon per video, sent when the video is LEFT BEHIND - swiped past,
  // closed, or finished - rather than a stream of progress pings, so a long
  // session still costs a handful of events.
  //
  // Position comes from the player itself (getCurrentTime), never from a wall
  // clock: a paused video, a stalled buffer or a backgrounded tab all keep
  // running on a clock and would report watch time nobody watched.
  var watching = null;      // the video currently on screen, or null

  function beginWatch() {
    var c = current();
    watching = c && c.id ? { id: c.id, title: c.title, ended: false } : null;
  }

  function flushWatch(reason) {
    if (!watching) return;
    var w = watching;
    watching = null;                       // never report the same view twice
    var p = slots[mid] && slots[mid].player;
    var secs = 0, total = 0;
    try {
      if (p && p.getCurrentTime) secs = p.getCurrentTime() || 0;
      if (p && p.getDuration) total = p.getDuration() || 0;
    } catch (e) { return; }
    // under a second is a mis-tap or a video that never started: reporting it
    // would drag every average down with views that never happened
    if (secs < 1) return;
    var pct = total > 0 ? Math.min(100, Math.round(secs / total * 100)) : 0;
    var done = w.ended || (total > 0 && pct >= 95);
    track('styling_video_watch', {
      item_id: w.id,
      item_name: w.title,
      seconds_watched: Math.round(secs),
      video_length: Math.round(total),
      percent_watched: pct,
      left_by: reason
    });
    // A second event, on purpose: counting completions in GA4 then needs no
    // custom metric set up first, which percent_watched would.
    if (done) track('styling_video_complete', { item_id: w.id, item_name: w.title });
  }

  // ── the playlist ───────────────────────────────────────────────────────────
  // Read fresh every time, so videos the wall has lazily appended are included.
  function collect() {
    var out = [];
    var tiles = document.querySelectorAll('[data-sm-wall-video]');
    for (var i = 0; i < tiles.length; i++) {
      var id = (tiles[i].getAttribute('data-sm-wall-video') || '').trim();
      if (!id) continue;
      out.push({
        id: id,
        title: tiles[i].getAttribute('data-sm-wall-title') || '',
        url: tiles[i].getAttribute('data-sm-wall-url') || '',
        price: tiles[i].getAttribute('data-sm-wall-price') || '',
        was: tiles[i].getAttribute('data-sm-wall-was') || '',
        off: tiles[i].getAttribute('data-sm-wall-off') || '',
        img: tiles[i].getAttribute('data-sm-wall-img') || ''
      });
    }
    return out;
  }

  function at(i) { return playlist[i] || null; }
  function current() { return at(index) || { id: '', title: '', url: '' }; }

  var cssDone = false;
  function injectCSS() {
    if (cssDone) return;
    cssDone = true;
    var style = document.createElement('style');
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  // ── building ───────────────────────────────────────────────────────────────
  function build() {
    injectCSS();

    overlay = document.createElement('div');
    overlay.className = 'sm-player';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Styling video');
    overlay.hidden = true;

    stack = document.createElement('div');
    stack.className = 'sm-player__stack';
    for (var i = 0; i < 3; i++) {
      var slide = document.createElement('div');
      slide.className = 'sm-player__slide';
      slide.innerHTML =
        '<img class="sm-player__poster" alt="" decoding="async">' +
        '<div class="sm-player__frame"><div id="sm-player-mount-' + i + '"></div></div>';
      stack.appendChild(slide);
      slots.push({ el: slide, mountId: 'sm-player-mount-' + i, player: null, videoId: '', ready: false });
    }
    overlay.appendChild(stack);

    chrome_ = document.createElement('div');
    chrome_.className = 'sm-player__chrome';
    chrome_.innerHTML =
      '<div class="sm-player__swipe"></div>' +
      '<div class="sm-player__tap">' + ICON.pause + '</div>' +
      '<button type="button" class="sm-player__btn sm-player__close" aria-label="Close video">' + ICON.close + '</button>' +
      '<button type="button" class="sm-player__btn sm-player__mute" aria-label="Unmute">' + ICON.muted + '</button>' +
      '<a class="sm-player__card" href="#">' +
        '<img class="sm-player__card-img" alt="" width="56" height="70" decoding="async">' +
        '<span class="sm-player__card-text">' +
          '<span class="sm-player__card-title"></span>' +
          '<span class="sm-player__card-prices">' +
            '<span class="sm-player__card-price"></span>' +
            '<s class="sm-player__card-was"></s>' +
            '<span class="sm-player__card-off"></span>' +
          '</span>' +
        '</span>' +
        '<svg class="sm-player__card-go" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 6l6 6-6 6"/></svg>' +
      '</a>';

    chrome_.querySelector('.sm-player__close').addEventListener('click', close);
    chrome_.querySelector('.sm-player__mute').addEventListener('click', ownMute);
    chrome_.querySelector('.sm-player__card').addEventListener('click', function () {
      var c = current();
      track('styling_video_product_click', { item_id: c.id, item_name: c.title });
      // sm-video-attribution.js picks this up and carries it to the order. It
      // is announced rather than called directly so the player keeps working
      // on its own if that file is ever removed.
      try {
        document.dispatchEvent(new CustomEvent('sm:video-product-click', {
          detail: { video: c.id, title: c.title, url: c.url, placement: 'fashiontok' }
        }));
      } catch (e) {}
    });
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
    setupSwipe(chrome_.querySelector('.sm-player__swipe'));

    document.body.appendChild(overlay);
    applyOrder();
  }

  // Rotation happens through CSS order, never by moving slides in the DOM:
  // moving an element that holds an iframe reloads it, which would throw away
  // the buffering that makes the next video start instantly.
  function applyOrder() {
    for (var k = 0; k < 3; k++) {
      slots[k].el.style.order = String((k - mid + 1 + 3) % 3);
    }
    slots[mid].el.appendChild(chrome_);
  }

  function slotFor(offset) { return slots[(mid + offset + 3) % 3]; }

  // ── the players ────────────────────────────────────────────────────────────
  function ready() { return window.YT && typeof window.YT.Player === 'function'; }

  function loadApi(then) {
    if (ready()) { then(); return; }
    if (!apiLoading) {
      apiLoading = true;
      var s = document.createElement('script');
      s.src = API;
      document.head.appendChild(s);
    }
    // Poll for YT itself rather than waiting on onYouTubeIframeAPIReady: if the
    // API finishes loading before that callback is registered it never fires.
    var tries = 0;
    var t = setInterval(function () {
      tries++;
      if (ready()) { clearInterval(t); then(); }
      else if (tries > 150) { clearInterval(t); }
    }, 100);
  }

  // `autoplay` false means cueVideoById: YouTube fetches the video and buffers
  // its start without playing, which is what makes the swipe instant.
  function mount(slot, videoId, autoplay) {
    if (!videoId) return;
    slot.videoId = videoId;

    if (slot.player) {
      if (autoplay) slot.player.loadVideoById(videoId);
      else if (typeof slot.player.cueVideoById === 'function') slot.player.cueVideoById(videoId);
      // neighbours are silent while they buffer; the centre takes the current
      // setting, because a re-pointed player keeps the mute state it had
      if (autoplay) applySound(slot); else { try { slot.player.mute(); } catch (e) {} }
      return;
    }

    slot.player = new window.YT.Player(slot.mountId, {
      videoId: videoId,
      host: 'https://www.youtube-nocookie.com',
      playerVars: {
        autoplay: autoplay ? 1 : 0, mute: 1, controls: 0, playsinline: 1,
        rel: 0, modestbranding: 1, fs: 0, iv_load_policy: 3
      },
      events: {
        onReady: function (e) {
          slot.ready = true;
          if (slot === slots[mid]) {
            // muted is how it has to start: browsers refuse autoplay with
            // sound. If the shopper had already turned sound on, restore it
            // once the player exists.
            if (soundOn) { try { e.target.unMute(); } catch (err) {} }
            e.target.playVideo();
          }
        },
        onStateChange: function (e) { onState(slot, e); },
        // Skip ONLY when the video genuinely is not there: 100 removed, 101 and
        // 150 embedding disabled by the owner. Codes 2 and 5 are a bad
        // parameter or a transient player problem, and skipping on those made
        // the feed jump ahead on its own while the shopper was watching.
        onError: function (e) {
          if (slot !== slots[mid]) return;
          if (e && (e.data === 100 || e.data === 101 || e.data === 150)) advance(1);
        }
      }
    });
  }

  function onState(slot, e) {
    var S = window.YT.PlayerState;
    if (slot !== slots[mid]) return;                 // off-screen players are cueing
    if (e.data === S.PLAYING) slot.el.classList.add('is-playing');
    if (e.data === S.ENDED) {
      // Only treat ENDED as "the video finished" if it actually played. A
      // player that cannot start reports ENDED straight away, and acting on
      // that raced forward through the whole feed without anything being
      // watched.
      var played = 0;
      try { played = slot.player.getCurrentTime() || 0; } catch (err) { played = 0; }
      if (played >= 1) {
        if (watching) watching.ended = true;
        advance(1);
        // the last video has nothing to advance to, so no swipe will flush it
        if (!at(index + 1)) flushWatch('ended');
      }
      return;
    }
    chrome_.classList.toggle('is-paused', e.data === S.PAUSED);
  }

  // load the centre video and cue its two neighbours
  function sync(playCentre) {
    var here = at(index), before = at(index - 1), after = at(index + 1);

    poster(slotFor(0), here);
    poster(slotFor(-1), before);
    poster(slotFor(1), after);

    if (!ready()) { loadApi(function () { sync(playCentre); }); return; }

    if (here) {
      var c = slotFor(0);
      if (c.videoId !== here.id) { c.el.classList.remove('is-playing'); mount(c, here.id, true); }
      else if (playCentre && c.player && c.player.playVideo) { c.player.playVideo(); }
    }
    if (after) { var n = slotFor(1); if (n.videoId !== after.id) { n.el.classList.remove('is-playing'); mount(n, after.id, false); } }
    if (before) { var p = slotFor(-1); if (p.videoId !== before.id) { p.el.classList.remove('is-playing'); mount(p, before.id, false); } }
  }

  function poster(slot, item) {
    var img = slot.el.querySelector('.sm-player__poster');
    if (item) {
      var src = THUMB + encodeURIComponent(item.id) + '/' + POSTER;
      if (img.getAttribute('src') !== src) {
        img.onerror = function () {
          this.onerror = null;
          // hqdefault is always 4:3, so it can no longer be used to judge the
          // video's shape - say so, and the mute decision is skipped
          this.setAttribute('data-poster', 'fallback');
          this.src = THUMB + encodeURIComponent(item.id) + '/hqdefault.jpg';
        };
        img.setAttribute('data-poster', 'frame0');
        img.setAttribute('src', src);
      }
      img.hidden = false;
    } else {
      img.onerror = null;
      img.removeAttribute('src');
      img.hidden = true;
    }
  }

  // An installed app has no tab strip. Handing it a _blank link pushes the
  // shopper out into the phone's browser and out of the app, which is a worse
  // interruption than the one opening a tab is meant to avoid - so inside the
  // installed app the product opens in place, as it always did.
  function inInstalledApp() {
    try {
      if (window.navigator && window.navigator.standalone === true) return true;
      return !!(window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
    } catch (e) { return false; }
  }

  function paintCard() {
    var item = current();
    var card = chrome_.querySelector('.sm-player__card');
    if (!item.url) { card.hidden = true; return; }
    card.setAttribute('href', item.url);
    // owner 2026-09-19: the product opens in a new tab so the feed keeps its
    // place. Closing the product returns the shopper to the video they were on,
    // mid scroll, instead of the top of the wall.
    if (inInstalledApp()) {
      card.removeAttribute('target');
      card.removeAttribute('rel');
    } else {
      card.setAttribute('target', '_blank');
      card.setAttribute('rel', 'noopener');
    }
    card.querySelector('.sm-player__card-title').textContent = item.title;
    card.querySelector('.sm-player__card-price').textContent = item.price;
    var was = card.querySelector('.sm-player__card-was');
    var off = card.querySelector('.sm-player__card-off');
    was.textContent = item.was;
    was.hidden = !item.was;
    off.textContent = item.off ? '-' + item.off + '%' : '';
    off.hidden = !item.off;
    var img = card.querySelector('.sm-player__card-img');
    if (item.img) { img.src = item.img; img.alt = item.title; img.hidden = false; }
    else { img.hidden = true; }
    card.hidden = false;
  }

  // ── moving between videos ──────────────────────────────────────────────────
  // one slide plus the gap: measured, never assumed, so changing the CSS cannot
  // desynchronise the animation from the layout
  function step_px() {
    var a = slots[mid].el.getBoundingClientRect();
    return a.height + parseFloat(getComputedStyle(stack).rowGap || 7);
  }

  function settle() {
    stack.style.transition = reduceMotion ? 'none' : 'transform 170ms ease-out';
    stack.style.transform = 'translateY(0)';
  }

  function land(dir) {
    // before anything moves: slots[mid] is still the video being left
    flushWatch('swipe');
    index += dir;
    mid = (mid + dir + 3) % 3;
    applyOrder();
    stack.style.transition = 'none';
    stack.style.transform = 'translateY(0)';
    paintCard();
    chrome_.classList.remove('is-paused');
    // pause whatever the old centre was, so two videos never play at once
    var oldSlot = slotFor(-dir);
    // the video leaving is the one the shopper was actually listening to, so
    // it settles the question before the answer is pushed onto the arrival
    if (oldSlot && oldSlot.player && typeof oldSlot.player.isMuted === 'function') {
      try { soundOn = !oldSlot.player.isMuted(); } catch (e) {}
    }
    applySound(slots[mid]);
    refreshMuteButton();
    if (oldSlot.player && oldSlot.player.pauseVideo) { try { oldSlot.player.pauseVideo(); } catch (e) {} }
    sync(true);
    track('styling_video_advance', { item_id: current().id, direction: dir > 0 ? 'next' : 'prev' });
    beginWatch();
  }

  function advance(dir) {
    if (sliding) return;
    if (!at(index + dir)) { settle(); return; }      // the ends stop, no wrap

    if (reduceMotion) { land(dir); return; }

    sliding = true;
    stack.style.transition = 'transform ' + SLIDE_MS + 'ms cubic-bezier(.22,.61,.36,1)';
    stack.style.transform = 'translateY(' + (step_px() * -dir) + 'px)';
    setTimeout(function () {
      land(dir);
      // a plain timeout, NOT requestAnimationFrame, which does not fire in a
      // background tab and left this lock stuck on for ever
      setTimeout(function () { sliding = false; }, 20);
    }, SLIDE_MS);
  }

  function togglePlay() {
    var p = slots[mid].player;
    if (!p || typeof p.getPlayerState !== 'function') return;
    if (p.getPlayerState() === window.YT.PlayerState.PLAYING) p.pauseVideo();
    else p.playVideo();
  }

  // The button is always present now, so this only keeps its icon honest.
  function refreshMuteButton() {
    paintMuteIcon();
  }

  function paintMuteIcon() {
    var btn = chrome_.querySelector('.sm-player__mute');
    var on = soundOn;
    btn.innerHTML = on ? ICON.loud : ICON.muted;
    btn.setAttribute('aria-label', on ? 'Mute' : 'Unmute');
  }

  function ownMute() {
    var p = slots[mid].player;
    if (!p || typeof p.unMute !== 'function') return;
    // flip OUR switch, not the player's answer: isMuted() lags a mute()
    // by a frame or two and a tap that reads it back gets ignored
    soundOn = !soundOn;
    applySound(slots[mid]);
    if (soundOn) track('styling_video_unmute', { item_id: current().id });
    setTimeout(paintMuteIcon, 60);
  }

  // soundOn is the single source of truth, and it is pushed onto whichever
  // player is centre. Reading it back off a player is only a sanity check:
  // YouTube's own mute exists on some layouts and not others, so where the
  // shopper can reach it we pick the change up on the way out.
  function soundIsOn(slot) {
    if (!slot || !slot.player || typeof slot.player.isMuted !== 'function') return false;
    try { return !slot.player.isMuted(); } catch (e) { return false; }
  }

  // Both directions. The old version only ever unmuted, so a slide that had
  // been unmuted once kept its sound for ever - loadVideoById re-points a
  // player but does not re-mute it.
  function applySound(slot) {
    if (!slot || !slot.player || typeof slot.player.unMute !== 'function') return;
    try { if (soundOn) slot.player.unMute(); else slot.player.mute(); } catch (e) {}
  }

  // ── gestures ───────────────────────────────────────────────────────────────
  function setupSwipe(swipe) {
    var y0 = 0, x0 = 0, t0 = 0, dragging = false, moved = false, lastTouch = 0;

    function begin(y, x) {
      if (sliding) return;
      dragging = true; moved = false;
      y0 = y; x0 = x; t0 = Date.now();
      stack.style.transition = 'none';
    }

    function move(y, x) {
      if (!dragging) return;
      var dy = y - y0;
      if (Math.abs(x - x0) > Math.abs(dy)) return;   // a horizontal drag is not ours
      if (Math.abs(dy) > 6) moved = true;
      // the finger moves the column one for one, so the neighbour it uncovers
      // tracks the finger exactly; at the two ends there is nothing to uncover,
      // so it barely gives
      var blocked = (dy > 0 && !at(index - 1)) || (dy < 0 && !at(index + 1));
      stack.style.transform = 'translateY(' + (blocked ? dy * 0.18 : dy) + 'px)';
    }

    function end(y, x) {
      if (!dragging) return;
      dragging = false;
      var dy = y - y0, dx = x - x0, dt = Date.now() - t0;
      var flick = Math.abs(dy) > 12 && dt < 220;     // a fast flick counts short
      if ((Math.abs(dy) >= SWIPE || flick) && Math.abs(dy) > Math.abs(dx)) {
        advance(dy < 0 ? 1 : -1);                    // finger up = next
      } else if (!moved && dt < TAP_MS) {
        settle();
        togglePlay();
      } else {
        settle();                                    // not far enough: spring back
      }
    }

    swipe.addEventListener('touchstart', function (e) {
      begin(e.touches[0].clientY, e.touches[0].clientX);
    }, { passive: true });
    swipe.addEventListener('touchmove', function (e) {
      move(e.touches[0].clientY, e.touches[0].clientX);
    }, { passive: true });
    swipe.addEventListener('touchend', function (e) {
      lastTouch = Date.now();
      end(e.changedTouches[0].clientY, e.changedTouches[0].clientX);
    }, { passive: true });
    swipe.addEventListener('touchcancel', function () { dragging = false; settle(); }, { passive: true });

    // desktop: drag with the mouse, exactly like the touch version
    swipe.addEventListener('mousedown', function (e) { e.preventDefault(); begin(e.clientY, e.clientX); });
    window.addEventListener('mousemove', function (e) { move(e.clientY, e.clientX); });
    window.addEventListener('mouseup', function (e) { if (dragging) end(e.clientY, e.clientX); });

    var wheelLock = 0;
    swipe.addEventListener('wheel', function (e) {
      if (Math.abs(e.deltaY) < 20) return;
      var now = Date.now();
      if (now - wheelLock < 420) return;   // one video per gesture, not ten
      wheelLock = now;
      advance(e.deltaY > 0 ? 1 : -1);
    }, { passive: true });
  }

  function onKey(e) {
    if (e.key === 'Escape' || e.key === 'Esc') { close(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); advance(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); advance(-1); }
    else if (e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); togglePlay(); }
  }

  // ── open / close ───────────────────────────────────────────────────────────
  function open(id) {
    if (!overlay) build();
    playlist = collect();
    if (!playlist.length) return;

    index = 0;
    for (var i = 0; i < playlist.length; i++) { if (playlist[i].id === id) { index = i; break; } }

    lastFocus = document.activeElement;
    paintCard();
    refreshMuteButton();
    stack.style.transition = 'none';
    stack.style.transform = 'translateY(0)';
    overlay.hidden = false;
    document.documentElement.classList.add('sm-player-open');
    chrome_.querySelector('.sm-player__close').focus();
    document.addEventListener('keydown', onKey);
    sync(true);

    track('styling_video_open', {
      item_id: id,
      item_name: current().title,
      videos_on_page: playlist.length
    });
    beginWatch();
  }

  function close() {
    if (!overlay || overlay.hidden) return;
    flushWatch('close');
    overlay.hidden = true;
    chrome_.classList.remove('is-paused');
    document.documentElement.classList.remove('sm-player-open');
    document.removeEventListener('keydown', onKey);
    // pause rather than destroy: the players and their buffers are reused, so
    // reopening is instant and nothing is fetched from YouTube a second time
    for (var k = 0; k < 3; k++) {
      var p = slots[k].player;
      if (p && p.pauseVideo) { try { p.pauseVideo(); } catch (e) {} }
    }
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  // Leaving the page is the commonest way a session ends, and 'close' is never
  // pressed. pagehide fires for a tab close, a back gesture and a bfcache
  // suspend alike, where unload no longer fires reliably on mobile Safari.
  window.addEventListener('pagehide', function () { flushWatch('left_page'); });

  document.addEventListener('click', function (e) {
    var tile = e.target.closest ? e.target.closest('[data-sm-wall-video]') : null;
    if (!tile) return;
    e.preventDefault();
    open(tile.getAttribute('data-sm-wall-video'));
  });

  // ── spread a product's videos apart ───────────────────────────────
  // The section loops PRODUCTS and emits each one's video ids together, so a
  // product with seven videos put seven identical-looking tiles in a row and
  // the visitor swiped through the same outfit seven times. Shopify sorts the
  // collection by product, so this cannot be fixed in the collection: the tiles
  // have to be re-ordered here.
  //
  // Order is still newest-product-first. A product's FIRST video keeps its
  // place; only its extra videos are pushed back, and only as far as they need
  // to go to leave SPREAD tiles between two videos of the same product. Where
  // there is nothing else left to place (the tail of a batch), a repeat is
  // allowed rather than dropping the tile.
  var SPREAD = 6;

  function tileKey(li) {
    var b = li.querySelector && li.querySelector('[data-sm-wall-url]');
    return (b && b.getAttribute('data-sm-wall-url')) || '';
  }

  // `seedTail` is the tiles already on the page. A batch that starts with the
  // same product the previous batch ended on would otherwise read as a repeat
  // across the join.
  function spreadTiles(nodes, seedTail) {
    var out = [], pending = [], last = {}, i, n, placed;

    if (seedTail) {
      for (i = 0; i < seedTail.length; i++) {
        var k = tileKey(seedTail[i]);
        if (k) last[k] = i - seedTail.length;   // negative: already behind us
      }
    }

    function free(li) {
      var k = tileKey(li);
      if (!k) return true;
      return !(k in last) || (out.length - last[k]) >= SPREAD;
    }
    function place(li) {
      var k = tileKey(li);
      if (k) last[k] = out.length;
      out.push(li);
    }

    var queue = [];
    for (i = 0; i < nodes.length; i++) queue.push(nodes[i]);

    while (queue.length || pending.length) {
      placed = false;
      // anything that has waited long enough goes first, oldest wait first
      for (i = 0; i < pending.length; i++) {
        if (free(pending[i])) { place(pending[i]); pending.splice(i, 1); placed = true; break; }
      }
      if (placed) continue;

      if (queue.length) {
        n = queue.shift();
        if (free(n)) place(n); else pending.push(n);
      } else {
        // nothing is eligible and there is nothing new left: take the one that
        // has waited longest. Better a repeat than a lost video.
        place(pending.shift());
      }
    }
    return out;
  }

  // Re-order a list in place. Appending a node that is already in the DOM moves
  // it, so no clone and no flicker.
  function applySpread(list) {
    if (!list) return;
    var kids = [], i;
    for (i = 0; i < list.children.length; i++) kids.push(list.children[i]);
    if (kids.length < 3) return;
    var ordered = spreadTiles(kids, null);
    for (i = 0; i < ordered.length; i++) list.appendChild(ordered[i]);
  }

  // ── the wall: load more as you scroll ──────────────────────────────────────
  // The section still renders real pagination links for crawlers and for
  // anyone without JavaScript. We hide them and pull the next page in instead.
  function setupInfiniteScroll() {
    var grid = document.querySelector('.sm-wall__grid');
    var sentinel = document.querySelector('.sm-wall__sentinel');
    if (!grid || !sentinel) return;

    var links = document.querySelector('.sm-wall__more');
    if (links) links.hidden = true;

    applySpread(grid);

    injectCSS();

    var spinner = document.createElement('div');
    spinner.className = 'sm-wall__spinner';
    spinner.hidden = true;
    sentinel.appendChild(spinner);

    var loading = false;
    var page = parseInt(sentinel.getAttribute('data-next-page'), 10) || 0;
    var total = parseInt(sentinel.getAttribute('data-total-pages'), 10) || 0;

    var io = null;

    function stop() {
      if (io) io.disconnect();
      window.removeEventListener('scroll', onScroll);
    }

    function loadNext() {
      if (loading || !page || page > total) return;
      loading = true;
      spinner.hidden = false;
      fetch(location.pathname + '?page=' + page, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var items = doc.querySelectorAll('.sm-wall__grid > li');
          var fresh = [];
          for (var i = 0; i < items.length; i++) {
            // The section deduplicates within one rendering, but it cannot see
            // the pages already on screen, and 155 videos are shared between
            // products. Skip anything already here.
            var btn = items[i].querySelector('[data-sm-wall-video]');
            var id = btn && btn.getAttribute('data-sm-wall-video');
            if (id && grid.querySelector('[data-sm-wall-video="' + id + '"]')) continue;
            fresh.push(document.importNode(items[i], true));
          }
          // Spread the new batch before it goes in, never after: re-ordering
          // tiles that are already on screen would shuffle the page under
          // whoever is reading it.
          var tail = [], kid = grid.lastElementChild, guard = 0;
          while (kid && guard < SPREAD) { tail.unshift(kid); kid = kid.previousElementSibling; guard++; }
          fresh = spreadTiles(fresh, tail);
          for (var f = 0; f < fresh.length; f++) grid.appendChild(fresh[f]);
          page++;
          sentinel.setAttribute('data-next-page', page);
          spinner.hidden = true;
          loading = false;
          if (page > total) { stop(); sentinel.parentNode.removeChild(sentinel); }
        })
        .catch(function () {
          // fetch failed: put the real pagination links back rather than
          // leaving the page with no way forward
          spinner.hidden = true;
          loading = false;
          stop();
          if (links) links.hidden = false;
        });
    }

    function near() {
      var r = sentinel.getBoundingClientRect();
      return r.top < window.innerHeight + 600;
    }

    // Two triggers on purpose. The observer is the efficient one, but its
    // callbacks are tied to rendering updates and never arrive while the tab is
    // in the background, so a throttled scroll check backs it up. Both funnel
    // into loadNext, which is guarded by `loading`.
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      setTimeout(function () {
        ticking = false;
        if (near()) loadNext();
      }, 150);
    }

    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) loadNext();
      }, { rootMargin: '600px 0px' });
      io.observe(sentinel);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    // Only after `load`, when layout is final. Run earlier and the grid can
    // still measure short, which made the page fetch page 2 before the visitor
    // had scrolled at all. This exists for the case where one page genuinely
    // does not fill the screen.
    window.addEventListener('load', function () { if (near()) loadNext(); });
  }

  function startWall() {
    // The home-page row renders one batch and has no sentinel, so it returns
    // early from setupInfiniteScroll. Spread it here instead.
    applySpread(document.querySelector('.sm-wall__row'));
    setupInfiniteScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startWall);
  } else {
    startWall();
  }

  // Changing any setting in the theme editor makes Shopify re-render the
  // section and swap its markup out. Our observer was still watching the old
  // sentinel, which no longer existed, so the load-as-you-scroll stopped and
  // the freshly rendered pagination links showed instead - which is what she
  // saw after turning off the product names. Re-arm on the editor's event.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('.sm-wall__grid')) {
      startWall();
    }
  });
})();


